// src/helpers/getUseCaseTree.ts

import { UseCaseRow, UseCaseNode } from 'types/schema';

export function getUseCaseTree(
  useCases: UseCaseRow[],
  levels = 1
): UseCaseNode[] {
  // 1) Build maps: id → node, system_name → id
  const nodeMap = new Map<string, UseCaseNode>();
  const sysNameToId = new Map<string, string>();

  useCases.forEach(u => {
    sysNameToId.set(u.system_name, u.use_case_id);
    nodeMap.set(u.use_case_id, {
      id:           u.use_case_id,
      systemName:   u.system_name,
      name:         u.name,
      description:  u.description,
      icon:         u.icon,
      sortOrder:    u.sort_order,
      children:     [],
      localization_name_key: u.localization_name_key,
      localization_description_key: u.localization_description_key
    });
  });

  // 2) Attach children using parent_system_name → look up parent ID
  useCases.forEach(u => {
    if (u.parent_system_name) {
      const parentId = sysNameToId.get(u.parent_system_name);
      const parent   = parentId && nodeMap.get(parentId);
      const node     = nodeMap.get(u.use_case_id);
      if (parent && node) parent.children.push(node);
    }
  });

  // 3) Sort all children arrays
  nodeMap.forEach(n => {
    n.children.sort((a, b) => a.sortOrder - b.sortOrder);
  });

  // 4) Recursive trim to limit depth
  function trim(node: UseCaseNode, depth: number): UseCaseNode {
    if (depth >= levels) {
      return { ...node, children: [] };
    }
    return {
      ...node,
      children: node.children.map(c => trim(c, depth + 1)),
    };
  }

  // 5) Pick roots by parent_system_name === null, sort & trim
  return useCases
    .filter(u => u.parent_system_name === null)
    .map(u => {
      const root = nodeMap.get(u.use_case_id)!;
      return trim(root, 0);
    })
    .sort((a, b) => a.sortOrder - b.sortOrder);
}
